import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Loader2, UserCheck, UserPlus, ChevronsUpDown, Check, X } from "lucide-react";
import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { cn } from "@/lib/utils";
import { useDebounce } from "@/hooks/use-debounce";

const formSchema = z.object({
  id: z.string().optional(),
  email: z.string().email("Valid email is required"),
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  role: z.enum(["admin", "staff", "service_bureau", "direct_client"]),
  companyId: z.number().optional(),
  authType: z.enum(["replit", "internal"]).default("internal"),
  profileImageUrl: z.string().optional(),
  passwordHash: z.string().optional(),
  tempPassword: z.boolean().optional(),
  lastLogin: z.date().optional(),
  contactId: z.number().optional(),
});

type FormData = z.infer<typeof formSchema>;

interface CreateUserModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: FormData) => void;
  isLoading: boolean;
}

function defaultAuthTypeForRole(role: string): "replit" | "internal" {
  return "internal";
}

export default function CreateUserModal({
  open,
  onClose,
  onSubmit,
  isLoading,
}: CreateUserModalProps) {
  const [authType, setAuthType] = useState<"replit" | "internal">("internal");
  const [contactMode, setContactMode] = useState<"searching" | "matched" | "create">("searching");
  const [selectedContact, setSelectedContact] = useState<any>(null);
  const [contactPhone, setContactPhone] = useState("");

  const [companyOpen, setCompanyOpen] = useState(false);
  const [companySearch, setCompanySearch] = useState("");
  const [selectedCompanyName, setSelectedCompanyName] = useState("");

  const [contactOpen, setContactOpen] = useState(false);
  const [contactSearch, setContactSearch] = useState("");

  const debouncedCompanySearch = useDebounce(companySearch, 250);
  const debouncedContactSearch = useDebounce(contactSearch, 250);

  const form = useForm<z.input<typeof formSchema>, unknown, FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      id: "",
      email: "",
      firstName: "",
      lastName: "",
      role: "service_bureau",
      companyId: undefined,
      authType: "internal",
    },
  });

  const watchRole = form.watch("role");
  const showCompanyField = watchRole === "service_bureau" || watchRole === "direct_client";
  const isServiceBureau = watchRole === "service_bureau";

  const { data: companyResults = [], isLoading: companiesLoading } = useQuery({
    queryKey: ["/api/companies", { context: "select", search: debouncedCompanySearch }],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (debouncedCompanySearch) params.set("search", debouncedCompanySearch);
      params.set("limit", "50");
      const res = await fetch(`/api/companies?${params.toString()}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch companies");
      return res.json();
    },
    select: (d: any) => d?.data ?? (Array.isArray(d) ? d : []),
    enabled: showCompanyField && companyOpen,
  });

  const { data: contactResults = [], isLoading: contactsLoading } = useQuery({
    queryKey: ["/api/contacts", { context: "select", search: debouncedContactSearch }],
    queryFn: async () => {
      const params = new URLSearchParams({ include: "companies" });
      if (debouncedContactSearch) params.set("search", debouncedContactSearch);
      params.set("limit", "50");
      const res = await fetch(`/api/contacts?${params.toString()}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch contacts");
      return res.json();
    },
    select: (d: any) => d?.data ?? (Array.isArray(d) ? d : []),
    enabled: showCompanyField && contactOpen,
  });

  const resetState = () => {
    setAuthType("internal");
    setContactMode("searching");
    setSelectedContact(null);
    setContactPhone("");
    setCompanyOpen(false);
    setCompanySearch("");
    setSelectedCompanyName("");
    setContactOpen(false);
    setContactSearch("");
  };

  useEffect(() => {
    if (open) {
      form.reset({
        id: "",
        email: "",
        firstName: "",
        lastName: "",
        role: "service_bureau",
        companyId: undefined,
        authType: "internal",
      });
      resetState();
    }
  }, [open]);

  const handleRoleChange = (role: string) => {
    const newAuthType = defaultAuthTypeForRole(role);
    form.setValue("role", role as FormData["role"]);
    form.setValue("authType", newAuthType);
    setAuthType(newAuthType);
    setContactMode("searching");
    setSelectedContact(null);
    setContactPhone("");
    if (role !== "service_bureau" && role !== "direct_client") {
      form.setValue("companyId", undefined);
      form.setValue("contactId", undefined);
      setSelectedCompanyName("");
      setSelectedContact(null);
      setContactMode("searching");
      setContactSearch("");
    }
  };

  const handleAuthTypeChange = (value: "replit" | "internal") => {
    setAuthType(value);
    form.setValue("authType", value);
    form.setValue("id", "");
    setContactMode("searching");
    setSelectedContact(null);
    setContactPhone("");
  };

  const handleSubmit = (data: FormData) => {
    const currentRole = form.getValues("role");
    if ((currentRole === "service_bureau" || currentRole === "direct_client") && !data.companyId) {
      form.setError("companyId", { message: "Please select a company" });
      return;
    }
    const submitData = {
      ...data,
      contactPhone: contactMode === "create" ? contactPhone : undefined,
      createContact: contactMode === "create",
    };
    onSubmit(submitData as any);
  };

  const handleClose = () => {
    form.reset({
      id: "",
      email: "",
      firstName: "",
      lastName: "",
      role: "service_bureau",
      companyId: undefined,
      authType: "internal",
    });
    resetState();
    onClose();
  };

  const handleSelectContact = (contact: any) => {
    setSelectedContact(contact);
    setContactMode("matched");
    setContactOpen(false);
    setContactSearch("");
    form.setValue("contactId", contact.id);

    if (contact.firstName) form.setValue("firstName", contact.firstName);
    if (contact.lastName) form.setValue("lastName", contact.lastName);
    if (contact.email) form.setValue("email", contact.email);

    if (contact.companies?.length === 1) {
      const rel = contact.companies[0];
      form.setValue("companyId", rel.companyId);
      setSelectedCompanyName(rel.company?.name ?? "");
    }
  };

  const handleClearContact = () => {
    setSelectedContact(null);
    setContactMode("searching");
    form.setValue("contactId", undefined);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create New User</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">

            {/* Role selector */}
            <FormField
              control={form.control}
              name="role"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Role</FormLabel>
                  <Select onValueChange={(v) => handleRoleChange(v)} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a role" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="service_bureau">Service Bureau</SelectItem>
                      <SelectItem value="direct_client">Direct Client</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="staff">Staff</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Auth type — locked for service_bureau, editable otherwise */}
            <FormField
              control={form.control}
              name="authType"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Authentication Type</FormLabel>
                  {isServiceBureau ? (
                    <>
                      <Select value="internal" disabled>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="internal">Internal (Email + Password)</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">
                        Service bureau accounts require internal authentication for portal access.
                      </p>
                    </>
                  ) : (
                    <Select onValueChange={handleAuthTypeChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select authentication type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="replit">Replit (External)</SelectItem>
                        <SelectItem value="internal">Internal (Email + Password)</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Info banners */}
            {authType === "replit" ? (
              <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-md text-sm">
                <p className="font-medium text-blue-900 dark:text-blue-100">External User Access:</p>
                <p className="text-blue-800 dark:text-blue-200 mt-1">
                  The user will sign in using their Replit account. When they first log in, their account will be automatically linked.
                </p>
              </div>
            ) : (
              <>
                <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-md text-sm">
                  <p className="font-medium text-green-900 dark:text-green-100">User Managed Credentials:</p>
                  <p className="text-green-800 dark:text-green-200 mt-1">
                    The system will send an invite email. The user will set their own password on first login.
                  </p>
                </div>

                {watchRole !== "service_bureau" && watchRole !== "direct_client" && (
                  <FormField
                    control={form.control}
                    name="id"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Employee ID (Optional)</FormLabel>
                        <FormControl>
                          <Input placeholder="Enter employee ID or leave blank for auto-generated" {...field} />
                        </FormControl>
                        <FormMessage />
                        <p className="text-sm text-muted-foreground">
                          System will auto-generate a unique ID if left blank.
                        </p>
                      </FormItem>
                    )}
                  />
                )}
              </>
            )}

            {/* ── Contact lookup (service_bureau / direct_client only) ─────────────
                Shown first so selecting a contact pre-fills name, email, and company. */}
            {showCompanyField && (
              <div className="space-y-2">
                <Label>
                  Link to Existing Contact{" "}
                  <span className="text-muted-foreground font-normal">(optional)</span>
                </Label>

                {contactMode === "matched" && selectedContact && (
                  <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-900/20 rounded-md border border-green-200 dark:border-green-800">
                    <UserCheck className="h-5 w-5 text-green-600 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-green-900 dark:text-green-100">
                        {selectedContact.firstName} {selectedContact.lastName}
                      </p>
                      <p className="text-xs text-green-700 dark:text-green-300 truncate">
                        {selectedContact.email}
                        {selectedContact.phone && ` • ${selectedContact.phone}`}
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={handleClearContact}
                      className="h-7 w-7 text-green-600 hover:text-green-700 shrink-0"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                )}

                {contactMode === "create" && (
                  <div className="space-y-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-md border border-blue-200 dark:border-blue-800">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <UserPlus className="h-5 w-5 text-blue-600" />
                        <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                          New contact will be created
                        </p>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setContactMode("searching")}
                        className="text-blue-600 hover:text-blue-700 text-xs"
                      >
                        Link existing instead
                      </Button>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="contactPhone" className="text-xs">
                        Phone Number (Optional)
                      </Label>
                      <Input
                        id="contactPhone"
                        value={contactPhone}
                        onChange={(e) => setContactPhone(e.target.value)}
                        placeholder="(555) 555-5555"
                        className="h-8 text-sm"
                      />
                    </div>
                  </div>
                )}

                {contactMode === "searching" && (
                  <div className="space-y-2">
                    <Popover open={contactOpen} onOpenChange={setContactOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          className="w-full justify-between text-muted-foreground font-normal"
                        >
                          Search by name or email...
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[400px] p-0" align="start">
                        <Command shouldFilter={false}>
                          <CommandInput
                            placeholder="Type a name or email to search..."
                            value={contactSearch}
                            onValueChange={setContactSearch}
                          />
                          <CommandList>
                            {contactsLoading ? (
                              <div className="flex items-center justify-center py-4">
                                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                              </div>
                            ) : contactSearch.length === 0 ? (
                              <CommandEmpty>Type a name or email to search...</CommandEmpty>
                            ) : contactResults.length === 0 ? (
                              <CommandEmpty>No contact found.</CommandEmpty>
                            ) : (
                              <CommandGroup>
                                {contactResults.slice(0, 50).map((contact: any) => (
                                  <CommandItem
                                    key={contact.id}
                                    value={contact.id.toString()}
                                    onSelect={() => handleSelectContact(contact)}
                                  >
                                    <div className="flex flex-col">
                                      <span className="font-medium">
                                        {contact.firstName} {contact.lastName}
                                      </span>
                                      {contact.email && (
                                        <span className="text-muted-foreground text-xs">
                                          {contact.email}
                                          {contact.companies?.length === 1 && (
                                            <> · {contact.companies[0].company?.name}</>
                                          )}
                                        </span>
                                      )}
                                    </div>
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            )}
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="w-full text-muted-foreground text-xs justify-start pl-1"
                      onClick={() => setContactMode("create")}
                    >
                      <UserPlus className="mr-1 h-3 w-3" />
                      Create a new contact instead
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Name fields */}
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="firstName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>First Name</FormLabel>
                    <FormControl>
                      <Input placeholder="John" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="lastName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Last Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Doe" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Email */}
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="john.doe@example.com" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Company — shown for service_bureau and direct_client */}
            {showCompanyField && (
              <FormField
                control={form.control}
                name="companyId"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>Company</FormLabel>
                    <Popover open={companyOpen} onOpenChange={setCompanyOpen}>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant="outline"
                            role="combobox"
                            aria-expanded={companyOpen}
                            className={cn(
                              "w-full justify-between",
                              !field.value && "text-muted-foreground"
                            )}
                            data-testid="select-company"
                          >
                            {selectedCompanyName || "Search for a company..."}
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-[400px] p-0" align="start">
                        <Command shouldFilter={false}>
                          <CommandInput
                            placeholder="Type to search companies..."
                            value={companySearch}
                            onValueChange={setCompanySearch}
                            data-testid="input-company-search"
                          />
                          <CommandList>
                            {companiesLoading ? (
                              <div className="flex items-center justify-center py-4">
                                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                              </div>
                            ) : companySearch.length === 0 ? (
                              <CommandEmpty>Type to search companies...</CommandEmpty>
                            ) : companyResults.length === 0 ? (
                              <CommandEmpty>No company found.</CommandEmpty>
                            ) : (
                              <CommandGroup>
                                {companyResults.slice(0, 50).map((company: any) => (
                                  <CommandItem
                                    key={company.id}
                                    value={company.id.toString()}
                                    onSelect={() => {
                                      field.onChange(company.id);
                                      setSelectedCompanyName(company.name);
                                      setCompanyOpen(false);
                                      setCompanySearch("");
                                    }}
                                    data-testid={`company-option-${company.id}`}
                                  >
                                    <Check
                                      className={cn(
                                        "mr-2 h-4 w-4",
                                        field.value === company.id ? "opacity-100" : "opacity-0"
                                      )}
                                    />
                                    {company.name}
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            )}
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Create User
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
